import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  AlertCircle,
  CheckCircle2,
  Shield,
  UserCog,
  Eye,
  EyeOff,
  Mail,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: "super-admin" | "admin" | "editor";
  personal_email?: string;
  gmail_email?: string;
  created_at: string;
}

export default function AdminUsers() {
  const { user: currentUser } = useAdminAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    personal_email: "",
    gmail_email: "",
    password: "",
    role: "editor" as AdminUser["role"],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const token = localStorage.getItem("adminToken");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data);
      } else {
        setError(data.error || "Failed to load users");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", personal_email: "", gmail_email: "", password: "", role: "editor" });
    setModalOpen(true);
  };

  const openEdit = (u: AdminUser) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      personal_email: u.personal_email || "",
      gmail_email: u.gmail_email || "",
      password: "",
      role: u.role,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const url = editingUser
        ? `/api/admin/users/${editingUser.id}`
        : "/api/admin/users";
      const method = editingUser ? "PUT" : "POST";

      const body: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        personal_email: formData.personal_email,
        gmail_email: formData.gmail_email,
        role: formData.role,
      };
      if (formData.password) body.password = formData.password;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (res.ok) {
        setSuccess(editingUser ? "User updated successfully" : "User created successfully");
        setModalOpen(false);
        fetchUsers();
      } else {
        setError(data.error || "Operation failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess("User deleted successfully");
        fetchUsers();
      } else {
        setError(data.error || "Delete failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const roleBadge = (role: string) => {
    const styles: Record<string, string> = {
      "super-admin": "bg-red-100 text-red-700",
      admin: "bg-blue-100 text-blue-700",
      editor: "bg-green-100 text-green-700",
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${styles[role] || "bg-neutral-100"}`}>
        {role.replace("-", " ")}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">Management</span>
          </div>
          <h1 className="text-2xl font-bold text-primary">Admin Users</h1>
          <p className="text-neutral-400 text-sm mt-1">Manage admin accounts and access levels</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-accent/20"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-600 text-sm">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-green-600 text-sm">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-neutral-400 text-sm">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50/50">
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Name</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Email</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Mailbox</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Role</th>
                  <th className="text-left px-5 py-3.5 font-medium text-neutral-500">Created</th>
                  <th className="text-right px-5 py-3.5 font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-neutral-50 hover:bg-neutral-50/30 transition-colors">
                    <td className="px-5 py-4 font-medium text-primary">{u.name}</td>
                    <td className="px-5 py-4 text-neutral-600">{u.email}</td>
                    <td className="px-5 py-4">
                      {u.personal_email || u.gmail_email ? (
                        <div className="space-y-0.5">
                          {u.personal_email && <p className="text-neutral-600">{u.personal_email}</p>}
                          {u.gmail_email && (
                            <p className="text-xs text-neutral-400 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {u.gmail_email}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-neutral-300">Company default</span>
                      )}
                    </td>
                    <td className="px-5 py-4">{roleBadge(u.role)}</td>
                    <td className="px-5 py-4 text-neutral-400 text-xs">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-2 hover:bg-blue-50 text-neutral-400 hover:text-blue-600 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(u.id)}
                          className="p-2 hover:bg-red-50 text-neutral-400 hover:text-red-600 rounded-lg transition-colors"
                          title="Delete"
                          disabled={u.id === currentUser?.id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-primary">
                  {editingUser ? "Edit User" : "Add New User"}
                </h3>
                <button onClick={closeModal} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-neutral-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="john@bomtrackng.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Personal Mail
                  </label>
                  <input
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="samuel@bomtrackng.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Linked Gmail
                  </label>
                  <input
                    type="email"
                    value={formData.gmail_email}
                    onChange={(e) => setFormData({ ...formData, gmail_email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="samuel@gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminUser["role"] })}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
                  >
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                    {editingUser ? "New Password (leave blank to keep current)" : "Password"}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      className="w-full px-4 py-2.5 pr-10 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                      placeholder={editingUser ? "••••••••" : "Enter password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2.5 rounded-xl bg-accent hover:bg-accent/90 text-white transition-colors text-sm font-medium disabled:opacity-70"
                  >
                    {submitting ? "Saving..." : editingUser ? "Update User" : "Create User"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary">Delete User</h3>
                  <p className="text-sm text-neutral-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white transition-colors text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
